/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    alpha,
    Tooltip,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { dispatch, useSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { buildDrilldownTitle } from 'src/constants/reportStatusPalette';
import TreatmentLeadPopup from './TreatmentLeadPopup';
import DataScopeIndicator from './DataScopeIndicator';

const TABLE_NAME = 'Landing URL Table';

const ALL_COLUMNS = [
    { field: 'landingUrl', label: 'Landing Page URL' },
    { field: 'enquiries', label: 'Enquiries' },
    { field: 'CONSULTATION_BOOKED', label: 'Consult Booked' },
    { field: 'CONSULTATION_ATTENDED', label: 'Attended' },
    { field: 'TREATMENT_STARTED', label: 'Started' },
    { field: 'CLOSED', label: 'Completed Treatment' },
    { field: 'revenue', label: 'Revenue' },
    { field: 'conversionRate', label: 'Conversion' },
];

// Same palette as Source / Referral tables.
const STATUS_COLUMNS = [
    { id: 'CONSULTATION_BOOKED', label: 'Consult Booked', color: '#8b5cf6' },
    { id: 'CONSULTATION_ATTENDED', label: 'Attended', color: '#f59e0b' },
    { id: 'TREATMENT_STARTED', label: 'Started', color: '#10b981' },
    { id: 'CLOSED', label: 'Completed Treatment', color: '#64748b' },
];

const formatCurrency = (value) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
    return `£${value?.toFixed(0) || 0}`;
};

export default function LandingUrlTable({ practiceId, dateRange }) {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'enquiries', sort: 'desc' }]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState({
        status: '',
        statusLabel: '',
        landingUrl: '',
    });
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    const { landingUrlData } = useSelector((state) => state.analyticsReports || {});
    const tableData = landingUrlData?.data || [];
    const isLoading = landingUrlData?.isLoading || false;

    // Table preferences plumbing — mirrors SourceStatusTable / ReferralStatusTable.
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    useEffect(() => {
        if (userId) dispatch(getTablePreferences({ practiceUserId: userId }));
    }, [userId]);

    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    const isFilterActive = hiddenColumns.length > 0;

    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    // Data is fetched at the page level (AnalyticsReportsBySource) so a single
    // multi-practice dispatch deduped on _landingUrlFetchKey serves the table.
    // No internal dispatch / no unmount-clear — keeps the cache warm between
    // tab visits so the user sees the previous rows instantly.

    const filteredData = useMemo(() => {
        if (!searchQuery) return tableData;
        const q = searchQuery.toLowerCase();
        return tableData.filter((row) => (row.landingUrl || '').toLowerCase().includes(q));
    }, [tableData, searchQuery]);

    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rows = filteredData.map((row) => visibleCols.map((col) => {
            if (col.field === 'revenue') return formatCurrency(row.revenue || 0);
            if (col.field === 'conversionRate') return `${parseFloat(row.conversionRate) || 0}%`;
            return row[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('landing-urls', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, hiddenColumns, buildExportName, dateRange]);

    const handleCellClick = (row, status) => {
        setPopupData({
            status: status.id,
            statusLabel: status.label,
            landingUrl: row.landingUrl,
        });
        setPopupOpen(true);
    };

    const columns = useMemo(() => {
        const cols = [
            {
                field: 'landingUrl',
                headerName: 'Landing Page URL',
                flex: 1.6,
                renderCell: (params) => {
                    const isNoUrl = params.value === '(no landing URL)';
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    bgcolor: alpha('#6366f1', 0.1),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Iconify icon="mdi:link-variant" width={18} sx={{ color: '#6366f1' }} />
                            </Box>
                            <Tooltip title={params.value || ''} placement="top" arrow>
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: '13px',
                                        fontStyle: isNoUrl ? 'italic' : 'normal',
                                        color: isNoUrl ? '#94a3b8' : 'inherit',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {params.value || '—'}
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                },
            },
            {
                field: 'enquiries',
                headerName: 'Enquiries',
                flex: 0.8,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={() => value > 0 && handleCellClick(params.row, { id: 'NEW', label: 'Enquiries' })}
                            sx={{
                                bgcolor: value > 0 ? alpha('#3b82f6', 0.12) : 'transparent',
                                color: value > 0 ? '#3b82f6' : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 ? 'pointer' : 'default',
                                '&:hover': value > 0 ? { bgcolor: alpha('#3b82f6', 0.2) } : {},
                            }}
                        />
                    );
                },
            },
        ];

        STATUS_COLUMNS.forEach((status) => {
            cols.push({
                field: status.id,
                headerName: status.label,
                flex: 0.9,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={() => value > 0 && handleCellClick(params.row, status)}
                            sx={{
                                bgcolor: value > 0 ? alpha(status.color, 0.12) : 'transparent',
                                color: value > 0 ? status.color : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 ? 'pointer' : 'default',
                                '&:hover': value > 0 ? { bgcolor: alpha(status.color, 0.2) } : {},
                            }}
                        />
                    );
                },
            });
        });

        cols.push({
            field: 'revenue',
            headerName: 'Revenue',
            flex: 0.9,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                return (
                    <Chip
                        label={formatCurrency(value)}
                        size="small"
                        onClick={() => value > 0 && handleCellClick(params.row, { id: 'CLOSED', label: 'Revenue' })}
                        sx={{
                            bgcolor: value > 0 ? alpha('#10b981', 0.12) : 'transparent',
                            color: value > 0 ? '#10b981' : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                            cursor: value > 0 ? 'pointer' : 'default',
                            '&:hover': value > 0 ? { bgcolor: alpha('#10b981', 0.2) } : {},
                        }}
                    />
                );
            },
        });

        cols.push({
            field: 'conversionRate',
            headerName: 'Conversion',
            flex: 0.9,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = parseFloat(params.value) || 0;
                const color = rate >= 20 ? '#10b981' : '#f59e0b';
                return (
                    <Chip
                        label={`${rate}%`}
                        size="small"
                        sx={{
                            bgcolor: alpha(color, 0.12),
                            color,
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        });

        return cols;
    }, []);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Tooltip
                    title="Leads grouped by the landing-page URL captured at form submission — the entire_url metadata field shown on the lead-detail Meta source view. Resolved from lead_metadata first, falling back to lead_source.metadata. Leads with no entire_url fall under '(no landing URL)'."
                    placement="bottom"
                    arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 320 } } }}
                >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            Landing Page URLs
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search URL..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Schedule this report via email" arrow>
                        <IconButton
                            size="small"
                            onClick={() => navigate(PATH_DASHBOARD.analyticsReports.settings)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: alpha('#7c3aed', 0.08), borderColor: '#7c3aed' },
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={16} sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox size="small" checked={isVisible} sx={{ p: 0, mr: 1 }} />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            <DataScopeIndicator variant="source" />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    <DataGridPro
                        rows={filteredData}
                        columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        columnVisibilityModel={columnVisibilityModel}
                        autoHeight
                        disableRowSelectionOnClick
                        disableColumnMenu={false}
                        pagination
                        pageSizeOptions={[5, 10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        getRowId={(row) => row.id || row.landingUrl}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f1f5f9',
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: '#f8fafc',
                            },
                        }}
                    />
                </Box>
            )}

            {filteredData.length === 0 && !isLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No landing URL data available — leads in this period have no entire_url captured
                    </Typography>
                </Box>
            )}

            <TreatmentLeadPopup
                open={popupOpen}
                onClose={() => setPopupOpen(false)}
                practiceId={practiceId}
                treatmentName={buildDrilldownTitle(popupData.landingUrl, popupData.statusLabel, popupData.status, 'All Landing Pages')}
                landingUrlFilter={popupData.landingUrl || ''}
                status={popupData.status}
                dateRange={dateRange ? {
                    startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
                    endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
                } : null}
            />
        </Box>
    );
}

LandingUrlTable.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateRange: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    }),
};

LandingUrlTable.defaultProps = {
    practiceId: null,
    dateRange: null,
};
